import { Injectable } from '@angular/core';

import { Subject, Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { BroadcasterService } from '../broadcaster/broadcaster.service';
import { EConnectionStatus } from '../../enums/connection-status.enum';

interface IErrorQueue {
    errors: Array<{
        time: Date;
        value: any;
    }>;
    subscription: Subscription;
}

@Injectable()
export class ErrorHandlingService {
    private _errorMap: Map<number, IErrorQueue> = new Map();

    private _errorEmit: Subject<{ type: number; value: any }> = new Subject();

    constructor(protected broadcaster: BroadcasterService) {
        this._initError(EConnectionStatus.STATUS_400); // Bad request
        this._initError(EConnectionStatus.STATUS_401); // Unauthorized
        this._initError(EConnectionStatus.STATUS_403); // Forbidden
        this._initError(EConnectionStatus.STATUS_404); // Not Found
        this._initError(EConnectionStatus.STATUS_405); // Method Not Allowed
        this._initError(EConnectionStatus.STATUS_408); // Request Timeout
        this._initError(EConnectionStatus.STATUS_409); // Conflict
        this._initError(EConnectionStatus.STATUS_415); // Unsupported Media Type

        this._initError(EConnectionStatus.STATUS_500); // Internal Server Error
        this._initError(EConnectionStatus.STATUS_501); // Not Implemented
        this._initError(EConnectionStatus.STATUS_503); // Service Unavailable
    }

    public init(errorNumber: EConnectionStatus) {
        this._initError(errorNumber);
    }

    public getAnyError$(): Observable<any> {
        return this._errorEmit.pipe(map((error) => error.value));
    }

    public getError$(errorNumber: EConnectionStatus): Observable<any> {
        this._initError(errorNumber);
        return this._errorEmit.pipe(
            filter((error) => error.type === errorNumber),
            map((error) => error.value)
        );
    }

    public getErrors(errorNumber: EConnectionStatus): Array<{ time: Date; value: any }> {
        this._initError(errorNumber);
        return JSON.parse(JSON.stringify(this._errorMap.get(errorNumber).errors));
    }

    public setError(errorNumber: EConnectionStatus, value: any) {
        this._initError(errorNumber);
        this._errorMap.get(errorNumber).errors.push({ time: new Date(), value: value });
        this._errorEmit.next({ type: errorNumber, value: value });
    }

    private _initError(errorNumber: EConnectionStatus) {
        if (!this._errorMap.has(errorNumber)) {
            const _initError: IErrorQueue = {
                errors: [],
                subscription: this.broadcaster.on(errorNumber).subscribe((data) => {
                    if (data.hasOwnProperty('type') && data.hasOwnProperty('error')) {
                        this.setError(errorNumber, data);
                    }
                })
            };
            this._errorMap.set(errorNumber, _initError);
        }
    }
}
